import {
  lockIonContentScroll,
  unlockIonContentScroll,
} from '@/core/lib/ion-content-scroll-lock';

/**
 * Lock body and IonContent scroll during drag so the page doesn't scroll when dragging on mobile.
 * Call on drag start, restore on drag end.
 */
export function lockBodyScrollDuringDrag(): void {
  lockIonContentScroll();
}

export function unlockBodyScrollAfterDrag(): void {
  unlockIonContentScroll();
}

/**
 * Trigger haptic feedback for drag start (e.g. when user starts dragging after long-press).
 * Only runs on native (Capacitor); no-op on web.
 */
export async function triggerDragStartHaptic(): Promise<void> {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.impact({ style: ImpactStyle.Medium });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback for drag end / drop.
 * Only runs on native (Capacitor); no-op on web.
 */
export async function triggerDragEndHaptic(): Promise<void> {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.impact({ style: ImpactStyle.Light });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback when the dragged item moves over another drop target.
 * Only runs on native (Capacitor); no-op on web.
 */
export async function triggerDragOverHaptic(): Promise<void> {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.impact({ style: ImpactStyle.Light });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback for UI taps (e.g. nav bar, buttons).
 * Only runs on native (Capacitor); no-op on web.
 */
export async function triggerTapHaptic(): Promise<void> {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.impact({ style: ImpactStyle.Light });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback for successful actions (e.g. save, copy, form submit).
 * Uses notification type SUCCESS. Only runs on native (Capacitor); no-op on web.
 */
export async function triggerSuccessHaptic(): Promise<void> {
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.notification({ type: NotificationType.Success });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback for warnings (e.g. validation warning, caution).
 * Uses notification type WARNING. Only runs on native (Capacitor); no-op on web.
 */
export async function triggerWarningHaptic(): Promise<void> {
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.notification({ type: NotificationType.Warning });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback for errors or failed actions (e.g. API error, delete confirm).
 * Uses notification type ERROR. Only runs on native (Capacitor); no-op on web.
 */
export async function triggerErrorHaptic(): Promise<void> {
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.notification({ type: NotificationType.Error });
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/**
 * Trigger haptic feedback when a selection changes (e.g. tab switch, Select value, picker).
 * Uses the full selection sequence (start → changed → end) so feedback fires on iOS;
 * selectionChanged() alone does nothing without selectionStart() first.
 * Only runs on native (Capacitor); no-op on web.
 */
export async function triggerSelectionHaptic(): Promise<void> {
  try {
    const { Haptics } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.selectionStart();
      await Haptics.selectionChanged();
      await Haptics.selectionEnd();
    }
  } catch {
    // @capacitor/haptics or Capacitor not available (e.g. web)
  }
}

/** Begin a selection gesture (finger down on a scrubbable control). */
export async function triggerSelectionStartHaptic(): Promise<void> {
  try {
    const { Haptics } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.selectionStart();
    }
  } catch {
    // no-op on web / missing plugin
  }
}

/** Tick while scrubbing across selectable items. Requires selectionStart first. */
export async function triggerSelectionChangedHaptic(): Promise<void> {
  try {
    const { Haptics } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.selectionChanged();
    }
  } catch {
    // no-op on web / missing plugin
  }
}

/** End a selection gesture (finger up). */
export async function triggerSelectionEndHaptic(): Promise<void> {
  try {
    const { Haptics } = await import('@capacitor/haptics');
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      await Haptics.selectionEnd();
    }
  } catch {
    // no-op on web / missing plugin
  }
}
